import { Form, InputNumber } from 'antd';
import classNames from 'classnames';
import { Fragment, useContext } from 'react';

import { CurrencyContext } from 'app/components/commons/Currency/CurrencyContext/CurrencyContext';
import { FormLineLayout } from 'app/components/commons/Form/FormLineLayout/FormLineLayout';
import { ViewEditModeContext } from 'app/context/ViewEditModeContext/ViewEditModeContext';
import { PAX_TYPE } from 'app/typings/core/admin/enum/paxType';
import type { PriceDifferentiationModeType } from 'app/typings/core/common/openingConfiguration/priceConfigurationType';
import { PRICE_DIFFERENTIATION_MODE } from 'app/typings/core/common/openingConfiguration/priceConfigurationType';

import { useValidationErrors } from '../../../hook/useValidationStore';
import type { PriceValidationError } from '../../../hook/useValidationStore';

import { PriceButtons } from './PriceButtons';

const RULES = [{ required: true, message: 'Set a valid price value' }];

type PriceField = {
  name: string[];
  label: string;
};

const getPriceFields = (
  mode: PriceDifferentiationModeType = PRICE_DIFFERENTIATION_MODE.NONE,
  isOriginal: boolean,
  allowedPaxTypes?: string[],
  isAlwaysIncludedWithNight?: boolean
): PriceField[] => {
  const basePrefix = [
    'openingConfiguration',
    'priceConfiguration',
    'configuration',
  ];

  if (isOriginal) {
    return [
      {
        name: [...basePrefix, 'price'],
        label: 'Public rate',
      },
      {
        name: [...basePrefix, 'negotiatedPrice'],
        label: 'Negotiated rate',
      },
      {
        name: [...basePrefix, 'staycationPrice'],
        label: 'Staycation rate',
      },
    ];
  }

  const hasChildOnly =
    allowedPaxTypes?.length === 1 && allowedPaxTypes[0] === PAX_TYPE.CHILD;

  switch (mode) {
    case PRICE_DIFFERENTIATION_MODE.NONE:
      if (hasChildOnly) {
        return [
          {
            name: [...basePrefix, 'childPrice'],
            label: 'Child price',
          },
        ];
      }

      return [
        {
          name: [...basePrefix, 'price'],
          label: '',
        },
      ];

    case PRICE_DIFFERENTIATION_MODE.WITH_DAYTIME:
      if (isAlwaysIncludedWithNight) {
        if (hasChildOnly) {
          return [
            {
              name: [...basePrefix, 'childDaytimePrice'],
              label: 'Child day price',
            },
          ];
        }

        return [
          {
            name: [...basePrefix, 'daytimePrice'],
            label: 'Day time price',
          },
        ];
      }

      if (hasChildOnly) {
        return [
          {
            name: [...basePrefix, 'childPrice'],
            label: 'Child price',
          },
          {
            name: [...basePrefix, 'childDaytimePrice'],
            label: 'Child day price',
          },
        ];
      }

      return [
        {
          name: [...basePrefix, 'price'],
          label: 'Night stay price',
        },
        {
          name: [...basePrefix, 'daytimePrice'],
          label: 'Day time price',
        },
      ];

    case PRICE_DIFFERENTIATION_MODE.WITH_CHILD:
      return [
        {
          name: [...basePrefix, 'price'],
          label: 'Adult price',
        },
        {
          name: [...basePrefix, 'childPrice'],
          label: 'Child price',
        },
      ];

    case PRICE_DIFFERENTIATION_MODE.WITH_CHILD_DAYTIME:
      return [
        {
          name: [...basePrefix, 'price'],
          label: 'Adult night price',
        },
        {
          name: [...basePrefix, 'childPrice'],
          label: 'Child night price',
        },
        {
          name: [...basePrefix, 'daytimePrice'],
          label: 'Adult day price',
        },
        {
          name: [...basePrefix, 'childDaytimePrice'],
          label: 'Child day price',
        },
      ];

    default:
      return [];
  }
};

type ConstantPriceConfigurationProps = {
  isOriginal: boolean;
};

export const ConstantPriceConfiguration = ({
  isOriginal,
}: ConstantPriceConfigurationProps) => {
  const form = Form.useFormInstance();
  const priceDifferentiationMode = Form.useWatch(
    ['openingConfiguration', 'priceConfiguration', 'priceDifferentiationMode'],
    form
  );
  const formValues = Form.useWatch([], form);

  const { priceError } = useValidationErrors();

  const hasFieldError = (fieldName: string): boolean => {
    const constantError = (priceError.errors as PriceValidationError[]).find(
      (err) =>
        err.type === 'CONSTANT' &&
        err.missingFields &&
        err.missingFields.includes(fieldName)
    );

    return !!constantError;
  };

  const { currencySymbol, currencyFormatter } = useContext(CurrencyContext);
  const { isEditMode } = useContext(ViewEditModeContext);

  const isAlwaysIncludedWithNight = Form.useWatch(
    'isAlwaysIncludedWithNight',
    form
  );

  const allowedPax = Form.useWatch(['allowedPaxTypes'], form);

  const hasAdultOnly =
    allowedPax?.length === 1 && allowedPax[0] === PAX_TYPE.ADULT;
  const hasChildOnly =
    allowedPax?.length === 1 && allowedPax[0] === PAX_TYPE.CHILD;
  const hasBothPaxTypes = !hasAdultOnly && !hasChildOnly;

  const hasDayPrice =
    priceDifferentiationMode ===
      PRICE_DIFFERENTIATION_MODE.WITH_CHILD_DAYTIME ||
    priceDifferentiationMode === PRICE_DIFFERENTIATION_MODE.WITH_DAYTIME;

  const hasAdultChildPrice =
    hasBothPaxTypes &&
    (priceDifferentiationMode ===
      PRICE_DIFFERENTIATION_MODE.WITH_CHILD_DAYTIME ||
      priceDifferentiationMode === PRICE_DIFFERENTIATION_MODE.WITH_CHILD);

  const priceFields = getPriceFields(
    priceDifferentiationMode,
    isOriginal,
    allowedPax,
    isAlwaysIncludedWithNight
  ).filter((pf) => {
    if (!isAlwaysIncludedWithNight) {
      return true;
    }

    return !pf.label.toLowerCase().includes('night');
  });

  const renderPriceFields = () => {
    return priceFields.map((field) => {
      const fieldValue =
        field.name.reduce((obj, key) => obj?.[key], formValues) ??
        form.getFieldValue(field.name);

      const fieldName = field.name[field.name.length - 1];
      const hasError = hasFieldError(fieldName);

      return (
        <FormLineLayout
          key={field.name.join('.')}
          isRequired={field.label.trim().length > 0 ? true : false}
          label={field.label}
        >
          <div className="constant-price-input">
            {isEditMode ? (
              <Form.Item name={field.name} rules={RULES}>
                <InputNumber
                  type="number"
                  addonAfter={currencySymbol}
                  min={0}
                  status={hasError ? 'error' : ''}
                />
              </Form.Item>
            ) : (
              <span className="view-data">
                {currencyFormatter(fieldValue) || 'ø empty'}
              </span>
            )}
            <span className="constant-price-unit">/unit</span>
          </div>
        </FormLineLayout>
      );
    });
  };

  return (
    <>
      <div
        className={classNames([
          'constant-price-configuration',
          {
            'grid-layout':
              hasDayPrice && hasAdultChildPrice && !isAlwaysIncludedWithNight,
          },
        ])}
      >
        {renderPriceFields()}
      </div>

      {isEditMode && !priceError.isValid && (
        <FormLineLayout>
          <span className="form-error-message">
            Please fix the errors above to continue.
            {priceError.errors.map((error, index) => (
              <div key={index}>- {error.message}</div>
            ))}
          </span>
        </FormLineLayout>
      )}

      {!isOriginal && isEditMode && (
        <FormLineLayout>
          <PriceButtons
            priceDifferentiationMode={priceDifferentiationMode}
            disableNightToggle={!!isAlwaysIncludedWithNight}
          />
        </FormLineLayout>
      )}

      {isOriginal && (
        <Form.Item
          name={[
            'openingConfiguration',
            'priceConfiguration',
            'priceDifferentiationMode',
          ]}
          noStyle
        >
          <input type="hidden" />
        </Form.Item>
      )}
    </>
  );
};
